import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import HelpList from "./components/HelpList";
import HelpEditor from "./components/HelpEditor";
import { useHelpItems } from "./hooks/useHelpItems";
import { useHelpDelete } from "./hooks/useHelpDelete";
import { HelpItemPayload, HelpItem } from "./types/HelpTypes";
import { HelpCardItem } from "./components/HelpCards";

type HelpRouteParams = { id?: string };

export const Help: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<HelpRouteParams>();
  
  const {
    filteredItems,
    items,
    search,
    typeFilter,
    loading,
    setSearch,
    setTypeFilter,
    addItem,
    updateItem,
    deleteItem,
    reorderItems,
  } = useHelpItems();

  const {
    confirmOpen,
    openDeleteConfirm,
    closeDeleteConfirm,
    getPendingDeleteId,
  } = useHelpDelete();

  const handleAddItem = async (payload: HelpItemPayload) => {
    try {
      await addItem(payload);
      history.push("/help");
    } catch (error) {
      console.error('Error adding help item:', error);
      // Handle error (maybe show toast notification)
    }
  };

  const handleSaveItem = async (payload: HelpItemPayload) => {
    if (id) {
      try {
        await updateItem(id, payload);
        history.push("/help");
      } catch (error) {
        console.error('Error updating help item:', error);
        // Handle error (maybe show toast notification)
      }
    }
  };

  const handleDeleteItem = async () => {
    if (id) {
      try {
        await deleteItem(id);
        history.push("/help");
      } catch (error) {
        console.error('Error deleting help item:', error);
        // Handle error (maybe show toast notification)
      }
    }
  };

  const getCurrentHelpItem = (): HelpItemPayload | undefined => {
    if (id) {
      const currentItem = items.find(item => item.id === id);
      if (currentItem) {
        return {
          title: currentItem.title,
          description: currentItem.description,
          type: currentItem.type as "PDF" | "Video",
          url: currentItem.url,
          fileName: currentItem.fileName,
        };
      }
    }
    return undefined;
  };

  const handleDelete = async () => {
    const deleteId = getPendingDeleteId();
    if (deleteId !== null) {
      try {
        await deleteItem(deleteId);
        closeDeleteConfirm();
      } catch (error) {
        console.error('Error deleting help item:', error);
        // Handle error but still close the confirm dialog
        closeDeleteConfirm();
      }
    }
  };

  const handleManage = (id: string) => {
    history.push(`/help/${id}`);
  };

  const handleCreateNew = () => {
    history.push("/help/new");
  };

  const handleReorder = async (reorderedItems: HelpCardItem[]) => {
    try {
      // Convert HelpCardItem[] to HelpItem[] for the hook
      const convertedItems: HelpItem[] = reorderedItems.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description || '',
        type: item.type,
        fileName: item.fileName || '',
        url: item.url || '',
        order: item.order
      }));
      await reorderItems(convertedItems);
    } catch (error) {
      console.error('Error reordering help items:', error);
      // Error handling could be improved with toast notifications
    }
  };

  const isEditor = location.pathname.endsWith("/new") || Boolean(id);

  if (!isEditor) {
    return (
      <HelpList
        items={filteredItems}
        search={search}
        typeFilter={typeFilter}
        loading={loading}
        onSearchChange={setSearch}
        onTypeFilterChange={setTypeFilter}
        onCreateNew={handleCreateNew}
        onManage={handleManage}
        onDelete={openDeleteConfirm}
        onReorder={handleReorder}
        confirmOpen={confirmOpen}
        onCloseConfirm={closeDeleteConfirm}
        onConfirmDelete={handleDelete}
      />
    );
  }

  return (
    <HelpEditor 
      onAdd={handleAddItem}
      onSave={handleSaveItem}
      onDelete={handleDeleteItem}
      helpItem={getCurrentHelpItem()}
      loading={loading}
    />
  );
};

export default Help;


